import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { STATE_LABEL, useNova, View } from '../lib/store';
import { act, dial, setMyState } from '../lib/actions';
import { useTheme } from '../lib/theme';
import { Icon, Kbd } from './ui';

interface Command {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

/** Ctrl/Cmd + K: do anything by typing. Typing a number offers to call it. */
export function CommandPalette() {
  const open = useNova((s) => s.paletteOpen);
  const role = useNova((s) => s.user?.role);
  const breaks = useNova((s) => s.breakReasons);
  const people = useNova((s) => s.people);
  const agents = useNova((s) => s.agents);
  const me = useNova((s) => s.user?.sub);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const close = () => useNova.getState().set({ paletteOpen: false });
  const go = (view: View) => useNova.getState().set({ view });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        useNova.getState().set({ paletteOpen: !useNova.getState().paletteOpen });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [
      { id: 'available', label: 'Go available', run: () => void setMyState('available') },
      ...breaks.map((b) => ({ id: `break-${b.code}`, label: `Take a break: ${b.label.toLowerCase()}`, run: () => void setMyState('break', b.code) })),
      { id: 'away', label: 'Set away', run: () => void setMyState('away') },
      { id: 'cockpit', label: 'Open my work', run: () => go('cockpit') },
      { id: 'history', label: 'Open call history', run: () => go('history') },
      ...(role && role !== 'agent' ? [{ id: 'recordings', label: 'Search recordings', run: () => go('recordings') }] : []),
      { id: 'reports', label: 'Open reports', run: () => go('reports') },
      ...(role === 'admin' || role === 'platform_admin' ? [{ id: 'admin', label: 'Open the admin console', run: () => go('admin') }] : []),
      { id: 'theme-light', label: 'Switch to the light theme', run: () => useTheme.getState().choose('light') },
      { id: 'theme-dark', label: 'Switch to the dark theme', run: () => useTheme.getState().choose('dark') },
      { id: 'theme-system', label: 'Match the system theme', run: () => useTheme.getState().choose('system') },
    ];
    if (role && role !== 'agent' && role !== 'qa') list.splice(3, 0, { id: 'ops', label: 'Open live operations', run: () => go('ops') });
    const digits = q.replace(/[^\d]/g, '');
    if (digits.length >= 3 && /^[+\d\s]+$/.test(q.trim())) {
      list.unshift({
        id: 'call',
        label: `Call ${q.trim()}`,
        hint: 'Rings your softphone, then the number',
        run: () => void act(() => api('POST', '/calls/originate', { number: q.trim() })),
      });
      return list.slice(0, 1);
    }
    const needle = q.trim().toLowerCase();
    // "call jane" or just "jane": call a colleague by name
    const who = needle.replace(/^call\s+/, '');
    const colleagues: Command[] =
      who.length >= 2
        ? Object.values(people)
            .filter((p) => p.id !== me && p.extension && p.name.toLowerCase().includes(who))
            .slice(0, 5)
            .map((p) => {
              const state = (agents[p.id] ?? p.live)?.state ?? 'offline';
              return {
                id: `call-${p.id}`,
                label: `Call ${p.name}`,
                hint: `Ext. ${p.extension} · ${STATE_LABEL[state]}`,
                run: () => void dial(p.extension!),
              };
            })
        : [];
    return needle ? [...colleagues, ...list.filter((c) => c.label.toLowerCase().includes(needle))] : list;
  }, [q, breaks, role, people, agents, me]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-start bg-abyss/70 px-4 pt-[14vh] backdrop-blur-sm" onMouseDown={close}>
      <div
        role="dialog"
        aria-label="Command palette"
        className="glass mx-auto w-full max-w-[560px] overflow-hidden rounded-2xl shadow-2xl shadow-black/50"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') close();
              else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, commands.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === 'Enter' && commands[active]) {
                commands[active].run();
                close();
              }
            }}
            placeholder="Type a command or a number to call"
            className="w-full bg-transparent py-4 outline-none placeholder:text-ink-3"
            aria-label="Command"
          />
          <Kbd>Esc</Kbd>
        </div>
        <ul className="max-h-[50vh] overflow-auto p-2" role="listbox">
          {commands.length === 0 ? <li className="px-3 py-3 text-sm text-ink-3">Nothing matches "{q}".</li> : null}
          {commands.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  c.run();
                  close();
                }}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm ${i === active ? 'bg-veil/[0.07] text-ink' : 'text-ink-2'}`}
              >
                <span>{c.label}</span>
                {c.hint ? <span className="text-xs text-ink-3">{c.hint}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
